import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { normalizeStage } from "./appShared";
import { ChevronRight, Clock, FileText, Phone, Target } from "lucide-react";

export const ConsultantWorkbench = ({ prospects, session }) => {
  const myProspects = prospects;
  
  const callsBooked = myProspects.filter((p) => normalizeStage(p.stage) === "QUAL_CALL_BOOKED").length;
  const assessmentsInProgress = myProspects.filter((p) => normalizeStage(p.stage) === "ASSESSMENT_IN_PROGRESS").length;
  const projectsInDelivery = myProspects.filter((p) => normalizeStage(p.stage) === "PROJECT_IN_DELIVERY").length;
  const followUpsNeeded = myProspects.filter((p) => {
    const s = normalizeStage(p.stage);
    return s === "QUALIFIED" || s === "QUALIFIED_TO_BUY" || s === "NO_SHOW";
  }).length;
  
  const priorityActions = [
    ...myProspects.filter(p => normalizeStage(p.stage) === "QUAL_CALL_BOOKED").map(p => ({
      type: "call",
      label: `Qualification call with ${p.account?.name || "Unknown"}`,
      prospect: p,
      priority: "high"
    })),
    ...myProspects.filter(p => normalizeStage(p.stage) === "NO_SHOW").map(p => ({
      type: "followup",
      label: `Follow up on no-show: ${p.account?.name || "Unknown"}`,
      prospect: p,
      priority: "high"
    })),
    ...myProspects.filter(p => normalizeStage(p.stage) === "ASSESSMENT_IN_PROGRESS").map(p => ({
      type: "assessment",
      label: `Complete assessment for ${p.account?.name || "Unknown"}`,
      prospect: p,
      priority: "medium"
    })),
  ].slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="rounded-2xl border-border bg-foreground/5 p-4 backdrop-blur-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-400">
              <Phone className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-foreground">{callsBooked}</div>
              <div className="text-xs text-foreground/55">Calls booked</div>
            </div>
          </div>
        </Card>
        
        <Card className="rounded-2xl border-border bg-foreground/5 p-4 backdrop-blur-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#007a9e]/10 dark:bg-yellow-500/10 text-[#007a9e] dark:text-yellow-400">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-foreground">{assessmentsInProgress}</div>
              <div className="text-xs text-foreground/55">Assessments in progress</div>
            </div>
          </div>
        </Card>
        
        <Card className="rounded-2xl border-border bg-foreground/5 p-4 backdrop-blur-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-400">
              <Target className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-foreground">{projectsInDelivery}</div>
              <div className="text-xs text-foreground/55">Projects active</div>
            </div>
          </div>
        </Card>
        
        <Card className="rounded-2xl border-border bg-foreground/5 p-4 backdrop-blur-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#007a9e]/10 text-[#2a9ec0]">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-foreground">{followUpsNeeded}</div>
              <div className="text-xs text-foreground/55">Follow-ups needed</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="rounded-3xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-foreground">Priority Actions</div>
            <div className="text-xs text-foreground/55">Your next best actions</div>
          </div>
          <Badge className="rounded-full border-border bg-muted text-foreground/70">
            Today
          </Badge>
        </div>
        
        {priorityActions.length === 0 ? (
          <div className="py-8 text-center text-sm text-foreground/55">
            No priority actions right now. Great job!
          </div>
        ) : (
          <div className="space-y-2">
            {priorityActions.map((action, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 rounded-xl border border-border bg-muted/60 p-3 transition-colors hover:bg-muted"
              >
                <div className={[
                  "flex h-8 w-8 items-center justify-center rounded-lg",
                  action.priority === "high" ? "bg-red-500/10 text-red-400" : "bg-[#007a9e]/10 dark:bg-yellow-500/10 text-[#007a9e] dark:text-yellow-400"
                ].join(" ")}>
                  {action.type === "call" && <Phone className="h-4 w-4" />}
                  {action.type === "followup" && <Clock className="h-4 w-4" />}
                  {action.type === "assessment" && <FileText className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground truncate">{action.label}</div>
                  <div className="text-xs text-foreground/45">
                    {action.prospect.contact?.email || "No email"}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-foreground/35" />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
